import React from 'react';
import {
  GaugeContainer,
  GaugeValueArc,
  GaugeReferenceArc,
  useGaugeState,
  Gauge,
  gaugeClasses
} from '@mui/x-charts/Gauge';
import { Box } from '@mui/material';

interface GaugeChartProps {
  id?: string;
  width: number;
  height: number;
  start_angle: number;
  end_angle: number;
  value: number;
  text?: string;
  inner_radius?: number;
  value_arc_fill?: string;
  reference_arc_fill?: string;
  show_readiness_gauge?: boolean;
  font_size?: string | number;
}

/**
 * Reuseable Gauge Chart component from MUI capable of displaying in multiple modes.
 * @param {number} width Width of chart.
 * @param {number} height Height of chart.
 * @param {number} start_angle Angle of bottom left edge of Gauge relative to 0 degrees.
 * @param {number} end_angle Angle of bottom right edge of Gauge relative to 0 degrees.
 * @param {number} value Percentage value of chart up to 100.
 * @param {string | undefined} text Optionally show custom text in the center of the gauge.
 * @param {number | undefined} inner_radius Optionally set custom Gauge arc thickness.
 * @param {string | undefined} value_arc_fill Optionally set color of value section of arc.
 * @param {string | undefined} reference_arc_fill Optionally set color of background (unfilled) section of arc.
 * @param {boolean | undefined} show_readiness_gauge Optionally render Gauge with segments and needle according to Syntiro Readiness Gauge design.
 * @param {string | number | undefined} font_size Optionally set font size of text inside Gauge.
 * @returns
 */
const GaugePointer = (): React.ReactNode => {
  const {
    valueAngle,
    outerRadius,
    cx,
    cy
  }: { valueAngle: number | null; outerRadius: number | null; cx: number; cy: number } =
    useGaugeState();

  if (valueAngle === null) {
    return null;
  }

  const target: { x: number; y: number } = {
    x: cx + outerRadius * Math.sin(valueAngle),
    y: cy - outerRadius * Math.cos(valueAngle)
  };
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill="#0E3A5A" />
      <path d={`M ${cx} ${cy} L ${target.x} ${target.y}`} stroke="#0E3A5A" strokeWidth={3} />
    </g>
  );
};

const GaugeChart = ({
  id,
  width,
  height,
  start_angle,
  end_angle,
  value,
  text,
  inner_radius,
  value_arc_fill,
  reference_arc_fill,
  show_readiness_gauge,
  font_size
}: GaugeChartProps) => {
  return show_readiness_gauge ? (
    <Box id={`${id}`} sx={{ position: 'relative', width, height }}>
      <GaugeContainer
        width={width}
        height={height}
        startAngle={start_angle}
        endAngle={end_angle}
        value={100}
        innerRadius={inner_radius}
        sx={{ position: 'absolute', top: 0, left: 0 }}
      >
        <GaugeValueArc style={{ fill: '#388e3c' }} />
      </GaugeContainer>
      <GaugeContainer
        width={width}
        height={height}
        startAngle={start_angle}
        endAngle={end_angle}
        value={90}
        innerRadius={inner_radius}
        sx={{ position: 'absolute', top: 0, left: 0 }}
      >
        <GaugeValueArc style={{ fill: '#66bb6a' }} />
      </GaugeContainer>
      <GaugeContainer
        width={width}
        height={height}
        startAngle={start_angle}
        endAngle={end_angle}
        value={80}
        innerRadius={inner_radius}
        sx={{ position: 'absolute', top: 0, left: 0 }}
      >
        <GaugeValueArc style={{ fill: '#FFD500' }} />
      </GaugeContainer>
      <GaugeContainer
        width={width}
        height={height}
        startAngle={start_angle}
        endAngle={end_angle}
        value={50}
        innerRadius={inner_radius}
        sx={{ position: 'absolute', top: 0, left: 0 }}
      >
        <GaugeValueArc style={{ fill: '#f44336' }} />
      </GaugeContainer>
      <GaugeContainer
        width={width}
        height={height}
        startAngle={start_angle}
        endAngle={end_angle}
        value={value}
        innerRadius={inner_radius}
        sx={{ position: 'absolute', top: 0, left: 0 }}
      >
        <GaugeReferenceArc style={{ fill: 'transparent', stroke: '#ffffff' }} />
        <GaugeValueArc style={{ fill: 'transparent' }} />
        <GaugePointer />
      </GaugeContainer>
    </Box>
  ) : (
    <Gauge
      width={width}
      height={height}
      value={value}
      startAngle={start_angle}
      endAngle={end_angle}
      innerRadius={inner_radius}
      text={({ value }) => (text ? text : `${value ? Math.round(value) : 0}%`)}
      sx={{
        [`& .${gaugeClasses.valueText}`]: {
          fontSize: font_size ?? '1rem'
        },
        [`& tspan`]: {
          fill: value_arc_fill,
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500
        },
        [`& .${gaugeClasses.valueArc}`]: {
          fill: value_arc_fill
        },
        [`& .${gaugeClasses.referenceArc}`]: {
          fill: reference_arc_fill
        }
      }}
    />
  );
};

export default GaugeChart;
